"use client";

import { Check, Headphones, ArrowRight, ShieldCheck, Zap } from "lucide-react";
import { TELECALLING_CAMPAIGNS } from "@/lib/demos/telecallingData";

interface TelecallingServicesSectionProps {
  currency: "INR" | "USD";
  onSelectCampaign: (campaignId: string) => void;
}

export function TelecallingServicesSection({
  currency,
  onSelectCampaign,
}: TelecallingServicesSectionProps) {
  const sym = currency === "USD" ? "$" : "₹";

  return (
    <section className="py-16 sm:py-24 bg-slate-950/60 border-y border-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 text-xs font-semibold uppercase tracking-wider mb-3">
            Specialized BPO Workstreams
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Engineered for High-Acuity Dialing &amp; Retention
          </h2>
          <p className="text-slate-400 text-sm sm:text-base mt-3">
            Every program includes screened caller talent, cloud telephony infrastructure, continuous QA speech auditing, and seamless CRM integrations.
          </p>
        </div>

        {/* 4 Campaign Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {TELECALLING_CAMPAIGNS.map((campaign) => {
            const baseRate = currency === "USD" ? campaign.baseMonthlyRateUsd : campaign.baseMonthlyRateInr;

            return (
              <div
                key={campaign.id}
                className="rounded-2xl bg-slate-900/80 border border-slate-800 p-6 sm:p-8 flex flex-col justify-between hover:border-cyan-500/40 transition-all group hover:shadow-xl hover:shadow-cyan-950/20"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                      {campaign.badge}
                    </span>
                    <span className="text-[11px] font-medium text-slate-400 flex items-center gap-1">
                      <Zap className="w-3.5 h-3.5 text-cyan-400" />
                      {campaign.slaUptime}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">
                    {campaign.title}
                  </h3>
                  <p className="text-xs text-cyan-400/90 font-medium mt-1">
                    {campaign.tagline}
                  </p>
                  <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                    {campaign.description}
                  </p>

                  {/* Dialing stack badge */}
                  <div className="mt-4 p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80 text-[11px] text-slate-400 flex items-center justify-between">
                    <span className="font-semibold text-slate-300">Telephony Engine:</span>
                    <span className="text-cyan-400 font-medium">{campaign.recommendedDialer}</span>
                  </div>

                  {/* Feature Checklist */}
                  <div className="mt-5 space-y-2.5">
                    {campaign.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 text-xs text-slate-300">
                        <div className="w-4 h-4 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-2.5 h-2.5 text-cyan-400" />
                        </div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card Bottom / Price & CTA */}
                <div className="mt-8 pt-6 border-t border-slate-800/80 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider block font-semibold">
                      Starting Seat Rate
                    </span>
                    <div className="text-xl font-black text-white">
                      {sym}{baseRate.toLocaleString()}
                      <span className="text-xs font-normal text-slate-400"> / seat / mo</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onSelectCampaign(campaign.id)}
                    className="px-4 py-2 rounded-xl bg-cyan-500/15 hover:bg-cyan-500 text-cyan-300 hover:text-white font-bold text-xs tracking-wide border border-cyan-500/30 hover:border-transparent transition-all flex items-center gap-1.5"
                  >
                    <span>Configure Pod</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
